import { useEffect, useRef, useState, useCallback } from "react";
import { CHORD_CATALOG, type ExtendedChordQuality } from "../lib/chordEngine";

export interface RealtimeDetectedChord {
  name: string;
  root: number; // 0-11
  quality: ExtendedChordQuality;
  confidence: number;
  chromagram: number[];
}

export interface RealtimeDetectorState {
  isRunning: boolean;
  detectedChord: RealtimeDetectedChord | null;
  volume: number;
  chromagram: number[];
}

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// Pre-computar templates de acordes como vectores de 12 dimensiones
const CHORD_TEMPLATES = CHORD_CATALOG.map((chord) => ({
  quality: chord.quality,
  pitchClasses: chord.intervals.map((i) => i % 12),
}));

function frequencyToMidi(freq: number): number {
  return 69 + 12 * Math.log2(freq / 440);
}

/**
 * Construye un chromagrama usando PEAK PICKING + filtro de armónicos.
 * Solo usa los picos dominantes del espectro, eliminando ruido y armónicos.
 */
function buildChromagram(
  frequencyData: Uint8Array,
  sampleRate: number,
  fftSize: number
): number[] {
  const binSize = sampleRate / fftSize;
  const nyquist = sampleRate / 2;
  const MIN_FREQ = 60;
  const MAX_FREQ = 2500;

  // ─── PASO 1: Encontrar picos locales ───
  interface Peak { bin: number; freq: number; energy: number }
  const peaks: Peak[] = [];

  for (let i = 3; i < frequencyData.length - 3; i++) {
    const freq = i * binSize;
    if (freq < MIN_FREQ || freq > MAX_FREQ || freq > nyquist - 50) continue;

    const e = frequencyData[i];
    // Debe ser un pico local (mayor que 3 vecinos a cada lado)
    if (e > frequencyData[i - 1] && e > frequencyData[i - 2] && e > frequencyData[i - 3] &&
        e > frequencyData[i + 1] && e > frequencyData[i + 2] && e > frequencyData[i + 3]) {
      const energy = e / 255;
      if (energy > 0.04) { // Umbral mínimo para ser considerado pico
        peaks.push({ bin: i, freq, energy });
      }
    }
  }

  // ─── PASO 2: Atenuar armónicos de picos más graves ───
  const harmonicEnergy = new Array(frequencyData.length).fill(0);
  for (const peak of peaks) {
    // La energía de un pico se atenúa si es armónico de otro pico más grave
    let isHarmonic = false;
    for (const other of peaks) {
      if (other.bin >= peak.bin) continue;
      for (let h = 2; h <= 4; h++) {
        const expectedBin = other.bin * h;
        if (Math.abs(expectedBin - peak.bin) <= 2) {
          isHarmonic = true;
          break;
        }
      }
      if (isHarmonic) break;
    }
    harmonicEnergy[peak.bin] = isHarmonic ? peak.energy * 0.2 : peak.energy;
  }

  // ─── PASO 3: Construir chromagrama solo con picos limpios ───
  const chromagram = new Array(12).fill(0);
  for (let i = 0; i < harmonicEnergy.length; i++) {
    if (harmonicEnergy[i] < 0.01) continue;
    const freq = i * binSize;
    const midi = frequencyToMidi(freq);
    const pc = Math.round(midi) % 12;
    chromagram[pc] += harmonicEnergy[i];
  }

  // Normalizar por el máximo
  const max = Math.max(...chromagram);
  if (max > 0) {
    for (let i = 0; i < 12; i++) {
      chromagram[i] /= max;
    }
  }

  return chromagram;
}

function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  if (normA === 0 || normB === 0) return 0;
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}

/**
 * Match de acorde por similitud coseno con penalización por notas faltantes.
 * Prefiere acordes simples (menos notas) cuando las notas presentes coinciden.
 * Penaliza fuertemente templates donde faltan notas obligatorias.
 */
function matchChord(chromagram: number[]): RealtimeDetectedChord | null {
  let bestScore = 0;
  let bestMatch: { root: number; quality: ExtendedChordQuality } | null = null;

  // Umbral para considerar que una nota está "presente" en el chromagrama
  const PRESENCE_THRESHOLD = 0.35;

  for (const template of CHORD_TEMPLATES) {
    for (let root = 0; root < 12; root++) {
      const templateVec = new Array(12).fill(0);
      const activePcs: number[] = [];
      for (const pc of template.pitchClasses) {
        const absPc = (pc + root) % 12;
        templateVec[absPc] = 1;
        activePcs.push(absPc);
      }

      const rawScore = cosineSimilarity(chromagram, templateVec);

      // Contar notas presentes vs notas totales del template
      const totalNotes = activePcs.length;
      let presentNotes = 0;
      for (const pc of activePcs) {
        if (chromagram[pc] > PRESENCE_THRESHOLD) presentNotes++;
      }

      // Penalización AGRESIVA por notas faltantes
      const coverageRatio = presentNotes / totalNotes;
      const penalty = (1 - coverageRatio) * 0.85; // hasta 85% de penalización
      let score = rawScore * (1 - penalty);

      // Bonus por simplicidad: acordes con menos notas y buena cobertura ganan mucho
      if (coverageRatio >= 1.0 && totalNotes <= 4) {
        score *= 1.25; // 25% bonus para tríadas/tétradas EXACTAS
      } else if (coverageRatio >= 0.8 && totalNotes <= 4) {
        score *= 1.15;
      }

      // Bonus si la raíz es la nota MÁS fuerte del chromagrama
      const rootPc = root % 12;
      const maxChroma = Math.max(...chromagram);
      if (maxChroma > 0 && chromagram[rootPc] === maxChroma) {
        score *= 1.2; // 20% bonus si la raíz es dominante
      } else if (chromagram[rootPc] > 0.5) {
        score *= 1.1;
      }

      // Penalizar acordes complejos (más de 4 notas) con baja cobertura
      if (totalNotes > 4 && coverageRatio < 0.7) {
        score *= 0.5; // 50% penalización
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = { root, quality: template.quality };
      }
    }
  }

  if (!bestMatch || bestScore < 0.40) return null;

  const rootName = NOTE_NAMES[bestMatch.root];
  let suffix = "";
  switch (bestMatch.quality) {
    case "major": suffix = ""; break;
    case "minor": suffix = "m"; break;
    case "dim": suffix = "dim"; break;
    case "aug": suffix = "aug"; break;
    case "sus2": suffix = "sus2"; break;
    case "sus4": suffix = "sus4"; break;
    default: suffix = bestMatch.quality; break;
  }

  return {
    name: `${rootName}${suffix}`,
    root: bestMatch.root,
    quality: bestMatch.quality,
    confidence: bestScore,
    chromagram: [...chromagram],
  };
}

function formatChordName(root: number, quality: ExtendedChordQuality): string {
  const rootName = NOTE_NAMES[root];
  switch (quality) {
    case "major": return rootName;
    case "minor": return `${rootName}m`;
    case "dim": return `${rootName}dim`;
    case "aug": return `${rootName}aug`;
    case "sus2": return `${rootName}sus2`;
    case "sus4": return `${rootName}sus4`;
    default: return `${rootName}${quality}`;
  }
}

/**
 * Motor de lock para estabilizar detecciones de acordes en tiempo real.
 * Usa un sistema de "hold" (confirmación) + "lock" (bloqueo temporal)
 * para evitar flickering sin sacrificar velocidad de respuesta.
 *
 * Parámetros:
 * - holdThreshold: frames necesarios para confirmar un nuevo acorde (default 6)
 * - lockDuration: frames que dura el lock una vez activado (default 6)
 * - changeThreshold: frames de hold necesarios para forzar un cambio
 *                    de acorde durante lock activo (default 10)
 */
class ChordLockEngine {
  private holdCount = 0;
  private lastChord: RealtimeDetectedChord | null = null;
  private lockChord: RealtimeDetectedChord | null = null;
  private lockCount = 0;

  constructor(
    private holdThreshold = 6,
    private lockDuration = 6,
    private changeThreshold = 10
  ) {}

  process(rawDetected: RealtimeDetectedChord | null): RealtimeDetectedChord | null {
    if (this.lockChord && this.lockCount > 0) {
      this.lockCount--;

      if (rawDetected && rawDetected.name === this.lockChord.name) {
        // El mismo acorde se sigue detectando: renovar lock
        this.lockCount = this.lockDuration;
        return this.lockChord;
      }

      if (rawDetected && this.holdCount >= this.changeThreshold) {
        // Otro acorde ha sido sostenido lo suficiente: cambiar lock
        this.lockChord = rawDetected;
        this.lockCount = this.lockDuration;
        this.holdCount = 0;
        return rawDetected;
      }

      // Mantener acorde bloqueado
      return this.lockChord;
    }

    // Sin lock activo: acumular hold
    if (rawDetected) {
      if (this.lastChord && this.lastChord.name === rawDetected.name) {
        this.holdCount++;
      } else {
        this.holdCount = 1;
        this.lastChord = rawDetected;
      }

      if (this.holdCount >= this.holdThreshold) {
        // Confirmar acorde y activar lock
        this.lockChord = rawDetected;
        this.lockCount = this.lockDuration;
        return rawDetected;
      }

      return null; // Aún en hold, no reportar
    }

    // Sin detección: resetear hold
    this.holdCount = 0;
    this.lastChord = null;
    return null;
  }

  reset(): void {
    this.holdCount = 0;
    this.lastChord = null;
    this.lockChord = null;
    this.lockCount = 0;
  }
}

/**
 * Hook de detección de acordes en tiempo real por chromagrama.
 * Usa Web Audio API (AnalyserNode + FFT) para análisis a 60 FPS.
 * Latencia: ~100-200ms (una ventana FFT).
 */
export function useRealtimeChordDetector() {
  const [state, setState] = useState<RealtimeDetectorState>({
    isRunning: false,
    detectedChord: null,
    volume: 0,
    chromagram: new Array(12).fill(0),
  });

  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const callbackRef = useRef<((chord: RealtimeDetectedChord | null) => void) | null>(null);
  const silenceCountRef = useRef(0); // frames consecutivos de silencio
  const lockEngineRef = useRef(new ChordLockEngine());

  const start = useCallback(async (onChord?: (chord: RealtimeDetectedChord | null) => void) => {
    if (analyserRef.current) return; // Ya corriendo

    callbackRef.current = onChord || null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const ctx = new AudioContext();
      // CRÍTICO: resumir el contexto de audio. En Chrome empieza suspendido
      // hasta que el usuario interactúa o se llama resume().
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 8192;
      analyser.smoothingTimeConstant = 0.85;
      source.connect(analyser);
      analyserRef.current = analyser;

      setState((s) => ({ ...s, isRunning: true }));

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const update = () => {
        const a = analyserRef.current;
        if (!a) return;

        a.getByteFrequencyData(dataArray);
        const sampleRate = a.context.sampleRate;

        // Calcular volumen RMS
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / bufferLength) / 255;

        let detected: RealtimeDetectedChord | null = null;

        let chromagram = new Array(12).fill(0);

        if (rms > 0.015) {
          silenceCountRef.current = 0;

          chromagram = buildChromagram(dataArray, sampleRate, 8192);
          const rawDetected = matchChord(chromagram);

          // ─── LOCK DE ACORDE CENTRALIZADO ───
          // Usa ChordLockEngine para estabilizar detecciones sin flickering
          detected = lockEngineRef.current.process(rawDetected);

          setState({
            isRunning: true,
            detectedChord: detected,
            volume: rms,
            chromagram,
          });

          if (detected && callbackRef.current) {
            callbackRef.current(detected);
          }
        } else {
          // Silencio
          silenceCountRef.current++;
          lockEngineRef.current.reset();

          // Solo reportar silencio tras varios frames reales de silencio
          if (silenceCountRef.current >= 8) {
            setState({
              isRunning: true,
              detectedChord: null,
              volume: rms,
              chromagram: new Array(12).fill(0),
            });
            if (callbackRef.current) callbackRef.current(null);
          } else {
            // Durante transición de silencio, mantener chromagrama anterior
            setState((s) => ({ ...s, volume: rms }));
          }
        }

        rafRef.current = requestAnimationFrame(update);
      };

      rafRef.current = requestAnimationFrame(update);
    } catch (err) {
      console.error("[RealtimeChordDetector] Mic error:", err);
      setState((s) => ({ ...s, isRunning: false }));
    }
  }, []);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    analyserRef.current = null;
    streamRef.current = null;
    silenceCountRef.current = 0;
    lockEngineRef.current.reset();
    setState({
      isRunning: false,
      detectedChord: null,
      volume: 0,
      chromagram: new Array(12).fill(0),
    });
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { state, start, stop };
}
